import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { StickyNote2, History } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { HistoryModal } from "./HistoryModal";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import {
  extractCardiorespiratoryHistory,
  extractWaterIntakeHistory,
  extractDigestoryHistory,
  extractDefecationHistory,
  extractUrogenitalHistory,
  extractFeedingTypeHistory,
  extractNeurologicalHistory,
  extractExerciseIntoleranceHistory,
  extractFemalesHistory,
  extractDewormingHistory,
  extractAnamnesisObservationsHistory,
} from "@/fluxvet/utils/questionnaireResponseHelper";

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

/**
 * Props do componente ObservationsSection
 */
interface ObservationsSectionProps {
  control: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Observações - Sistemas corporais e observações adicionais
 * Exibe formulário estruturado com checkboxes, radio buttons e campos de texto
 * Inclui botões para visualizar histórico de cada campo
 * 
 * Seções incluídas:
 * - Cardiorrespiratório
 * - Ingestão de Água
 * - Digestório
 * - Defecação/Fezes
 * - Urogenital
 * - Alimentação
 * - Neurológico
 * - Intolerância ao Exercício
 * - Fêmeas
 * - Vermifugação
 * - Observações Gerais da Anamnese
 * 
 * @param control - Control do react-hook-form
 */
export const ObservationsSection: React.FC<ObservationsSectionProps> = ({
  control,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados de cada campo
  // Usa useMemo para evitar reprocessamento desnecessário
  const cardiorespiratoryHistory = useMemo(
    () => extractCardiorespiratoryHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const waterIntakeHistory = useMemo(
    () => extractWaterIntakeHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const digestoryHistory = useMemo(
    () => extractDigestoryHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const defecationHistory = useMemo(
    () => extractDefecationHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const urogenitalHistory = useMemo(
    () => extractUrogenitalHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const feedingTypeHistory = useMemo(
    () => extractFeedingTypeHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const neurologicalHistory = useMemo(
    () => extractNeurologicalHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const exerciseIntoleranceHistory = useMemo(
    () => extractExerciseIntoleranceHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const femalesHistory = useMemo(
    () => extractFemalesHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const dewormingHistory = useMemo(
    () => extractDewormingHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const anamnesisObservationsHistory = useMemo(
    () => extractAnamnesisObservationsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Estado único para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    title: string;
    history: any[];
  }>({
    isOpen: false,
    title: "",
    history: [],
  });

  /**
   * Função auxiliar para abrir o modal de histórico
   */
  const openHistoryModal = (title: string, history: any[]) => {
    setHistoryModal({ isOpen: true, title, history });
  };

  /**
   * Função auxiliar para fechar o modal de histórico
   */
  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, title: "", history: [] });
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: "calc(100vh - 400px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Grid container spacing={2}>
          {/* CARDIORRESPIRATÓRIO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    CARDIORRESPIRATÓRIO
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Cardiorrespiratório",
                        cardiorespiratoryHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="cardiorespiratory"
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {["Eupneia", "Bradpneia", "Taquipneia", "Dispneia"].map(
                        (option) => (
                          <FormControlLabel
                            key={option}
                            sx={{ minWidth: 130 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={field.value?.includes(option) || false}
                                onChange={(e) => {
                                  const newValue = e.target.checked
                                    ? [...(field.value || []), option]
                                    : (field.value || []).filter(
                                        (v) => v !== option
                                      );
                                  field.onChange(newValue);
                                }}
                                sx={{
                                  color: "#00A884",
                                  "&.Mui-checked": { color: "#00A884" },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">{option}</Typography>
                            }
                          />
                        )
                      )}
                    </FormGroup>
                  )}
                />
                <Controller
                  name="cardiorespiratoryOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Outras observações..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* ING. ÁGUA */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    ING. ÁGUA
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Ingestão de Água",
                        waterIntakeHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="waterIntake"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value || ""}
                      onChange={field.onChange}
                      row
                    >
                      {["Normodipsia", "Polipsia", "Oligodipsia", "Adipsia"].map(
                        (option) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            sx={{ minWidth: 130 }}
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  color: "#00A884",
                                  "&.Mui-checked": { color: "#00A884" },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">{option}</Typography>
                            }
                          />
                        )
                      )}
                    </RadioGroup>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* DIGESTÓRIO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    DIGESTÓRIO
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Digestório",
                        digestoryHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="digestory"
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {[
                        "Normorexia",
                        "Hiporexia",
                        "Anorexia",
                        "Polifagia",
                        "Coprofagia",
                        "Disfagia",
                        "Apetite seletivo",
                        "Êmese",
                        "Regurgitação",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          sx={{ minWidth: 150 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                <Controller
                  name="digestoryOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Outras observações..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* DEFECAÇÃO/FEZES */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    DEFECAÇÃO / FEZES
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Defecação/Fezes",
                        defecationHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="defecation"
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {[
                        "Normoquezia",
                        "Aquezia",
                        "Disquezia",
                        "Tenesmo",
                        "Diarreia",
                        "Hematoquezia",
                        "Melena",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          sx={{ minWidth: 130 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                <Controller
                  name="defecationOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Outras observações..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* UROGENITAL */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    UROGENITAL
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Urogenital",
                        urogenitalHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="urogenital"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value || ""}
                      onChange={field.onChange}
                      row
                    >
                      {["Secreção", "Sem secreção"].map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          sx={{ minWidth: 150 }}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <Controller
                  name="urogenitalDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Descreva..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* ALIMENTAÇÃO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    ALIMENTAÇÃO
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Alimentação",
                        feedingTypeHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="feedingType"
                  control={control}
                  render={({ field }) => (
                    <FormGroup>
                      {[
                        "Petiscos",
                        "Ração Úmida",
                        "Legumes/Vegetais",
                        "A. Natural",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                <Controller
                  name="feedingTypeDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Descreva detalhes da alimentação..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* NEUROLÓGICO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    NEUROLÓGICO
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Neurológico",
                        neurologicalHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="neurological"
                  control={control}
                  render={({ field }) => (
                    <FormGroup>
                      {[
                        "Inclinação da cabeça",
                        "Crise Epiléptica",
                        "Ataxia",
                        "Letargia",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* INTOLERÂNCIA AO EXERCÍCIO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    INTOLERÂNCIA AO EX.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Intolerância ao Exercício",
                        exerciseIntoleranceHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="exerciseIntolerance"
                  control={control}
                  render={({ field }) => (
                    <FormGroup>
                      {["Fadiga", "Síncope", "Cianose"].map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* FÊMEAS */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    FÊMEAS
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Fêmeas",
                        femalesHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="females"
                  control={control}
                  render={({ field }) => (
                    <FormGroup>
                      {[
                        "Galactorreia",
                        "Pseudociese",
                        "Alt. de ciclo estral",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* VERMIFUGAÇÃO */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="#00A884"
                  >
                    VERMIFUGAÇÃO
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Vermifugação",
                        dewormingHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="deworming"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value || ""}
                      onChange={field.onChange}
                      row
                    >
                      {["Sim", "Não"].map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          sx={{ minWidth: 100 }}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <Controller
                  name="dewormingDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Descreva o vermífugo aplicado, datas, protocolo..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* OBSERVAÇÕES GERAIS */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StickyNote2 sx={{ fontSize: 18, color: "#00A884" }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Observações Gerais
                  </Typography>
                </Stack>
                <Divider />
                <Controller
                  name="anamnesisObservations"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Observações adicionais"
                      placeholder="Observações gerais sobre a anamnese e sistemas..."
                      multiline
                      minRows={4}
                      maxRows={15}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Modal único de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={closeHistoryModal}
        title={historyModal.title}
        history={historyModal.history}
      />
    </>
  );
};

export default ObservationsSection;

